import { useEffect, useState } from 'react';
import {
  ChakraAutocompleteField,
  ChakraSelect,
  InputField,
} from 'app/components/Core';
import { Box, SimpleGrid } from '@chakra-ui/react';
import { Option } from 'types/general.types';
import { ChakraConfirmationDialog } from 'app/components/Dialogs';
import { ChakraSelectProps } from 'app/components/Core/ChakraSelect/types';
import { Control, Controller, Path, useForm, useWatch } from 'react-hook-form';
import { chakraComponents } from 'chakra-react-select';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { fieldValidationMessages } from 'app/constants';
import { noteValues, timeSignatureOptions } from '../SongDetailsForm/constants';
import {
  getDefaultValues,
  isCustomValue,
  TimeSignatureFormData,
} from './utils';

const validationSchema = yup.object().shape({
  numberOfBeats: yup
    .number()
    .required(fieldValidationMessages.REQUIRED)
    .typeError(fieldValidationMessages.TYPE_NUM)
    .integer(fieldValidationMessages.INTEGER)
    .min(1, fieldValidationMessages.MIN_NUM(1))
    .max(99, fieldValidationMessages.MAX_NUM(99)),
  noteValue: yup
    .object()
    .shape({ title: yup.string(), value: yup.string() })
    .required(fieldValidationMessages.REQUIRED)
    .nullable(),
});

interface TimeSignatureInputProps<
  Form extends { timeSignature: Option | null }
> {
  control: Control<Form>;
  name: Path<Form>;
  label: string;
  onChange?: () => void;
  disabled?: boolean;
  selectProps?: Partial<ChakraSelectProps>;
}

export const TimeSignatureInput = <
  Form extends { timeSignature: Option | null }
>({
  control,
  onChange,
  name,
  label,
  disabled,
  selectProps,
}: TimeSignatureInputProps<Form>) => {
  const [timeSigDialogOpen, setTimeSigDialogOpen] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const currentFieldValue = useWatch({ name, control }) as Option;

  const {
    control: modalFormControl,
    reset,
    handleSubmit,
    formState,
    setValue,
    register,
  } = useForm<TimeSignatureFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'all',
    defaultValues: getDefaultValues(currentFieldValue),
  });

  const { isValid, isDirty, errors } = formState;

  useEffect(() => {
    const newValues = getDefaultValues(currentFieldValue);
    reset(newValues);
  }, [currentFieldValue, reset, setValue]);

  useEffect(() => {
    if (onChange) {
      onChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFieldValue?.value]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, ref, ...rest } }) => {
        return (
          <>
            <ChakraSelect
              dataTestId="Time Signature"
              isDisabled={disabled}
              label={label}
              {...rest}
              value={value as Option}
              menuIsOpen={selectOpen}
              onMenuOpen={() => setSelectOpen(true)}
              onMenuClose={() => setSelectOpen(false)}
              chakraStyles={{
                option: (_, state) => ({
                  border: state.data.value === 'custom' ? '1px solid' : 'none',
                  borderColor: 'M.30',
                  color: 'M.70',
                  bg: state.isSelected || state.isFocused ? 'M.30' : 'M.20',
                }),
              }}
              onChange={(e) => {
                setSelectOpen(false);
                if (e && isCustomValue(e as Option)) {
                  setTimeSigDialogOpen(true);
                } else {
                  reset({ noteValue: null, numberOfBeats: '' });
                  onChange(e);
                }
              }}
              key={`time-sig-${(value as Option)?.value}`}
              options={[
                ...timeSignatureOptions,
                { title: 'Custom', value: 'custom' },
              ]}
              isClearable
              mainRef={ref}
              components={{
                SingleValue: ({ children, ...props }) => (
                  <chakraComponents.SingleValue {...props}>
                    {!value || value?.value === 'custom' ? '' : value.title}
                  </chakraComponents.SingleValue>
                ),
              }}
              {...selectProps}
            />
            <ChakraConfirmationDialog
              handleAction={handleSubmit(({ numberOfBeats, noteValue }) => {
                const customValue = `${numberOfBeats}/${noteValue?.value}`;
                onChange({ title: customValue, value: customValue });
                setTimeSigDialogOpen(false);
                setSelectOpen(false);
              })}
              onClose={() => {
                setTimeSigDialogOpen(false);
                if (!isCustomValue(value as Option)) {
                  reset({ noteValue: null, numberOfBeats: '' });
                }
              }}
              isOpen={timeSigDialogOpen}
              title="Time Signature"
              actionButtonText="Set"
              disableSubmitAction={!isValid || !isDirty}
            >
              <form autoComplete="off">
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <InputField
                      data-testid="Number of beats"
                      label="Number of beats"
                      {...register('numberOfBeats')}
                      isInvalid={Boolean(errors.numberOfBeats)}
                      errorMessage={errors.numberOfBeats?.message}
                    />
                  </Box>
                  <Box>
                    <ChakraAutocompleteField
                      control={modalFormControl}
                      dataTestId="Note value"
                      name="noteValue"
                      label="Note value"
                      options={noteValues.map((value) => ({
                        title: value,
                        value,
                      }))}
                      isClearable
                    />
                  </Box>
                </SimpleGrid>
              </form>
            </ChakraConfirmationDialog>
          </>
        );
      }}
    />
  );
};
